import { useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Round 104e (Jun 2026) — "ETF Ownership" reverse N-PORT lookup.
// Shows which ETFs hold this stock and at what portfolio weight.
// Renders in the Stock Metrics card deck for non-fund tickers.
const fmtVal = (v) => {
  if (v == null) return '--';
  const abs = Math.abs(v);
  if (abs >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  return `$${Math.round(v).toLocaleString()}`;
};

export const HeldByEtfsCard = ({ ticker, onSelectTicker }) => {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let alive = true;
    setData(null);
    setExpanded(false);
    axios.get(`${API}/stocks/${ticker}/held-by-etfs`, { withCredentials: true })
      .then(({ data: d }) => { if (alive) setData(d); })
      .catch(() => { if (alive) setData({ etf_count: 0, etfs: [] }); });
    return () => { alive = false; };
  }, [ticker]);

  if (!data || !data.etf_count) return null;

  const rows = expanded ? data.etfs : data.etfs.slice(0, 8);
  const maxPct = data.etfs[0]?.weight_pct || 1;

  return (
    <div className="rounded-lg border bg-card px-4 py-2 break-inside-avoid" data-testid="held-by-etfs-card">
      <div className="flex items-baseline justify-between mt-4 mb-1.5">
        <h4 className="text-[10px] font-bold uppercase tracking-widest text-sky-500 dark:text-sky-400">ETF Ownership</h4>
        <span className="text-[10px] text-muted-foreground" data-testid="held-by-etfs-count">
          held by {data.etf_count.toLocaleString()} ETF{data.etf_count === 1 ? '' : 's'}
        </span>
      </div>
      <div className="flex items-center justify-between pb-1 border-b border-border/30">
        <span className="text-[9px] uppercase tracking-wider text-muted-foreground">Fund</span>
        <span className="text-[9px] uppercase tracking-wider text-muted-foreground">Weight in fund</span>
      </div>
      {rows.map((e, i) => {
        const clickable = !!onSelectTicker;
        return (
          <div
            key={e.etf_ticker}
            className={`flex items-center gap-2 py-1 border-b border-border/20 last:border-0 ${clickable ? 'cursor-pointer hover:bg-muted/40 -mx-1 px-1 rounded transition-colors' : ''}`}
            onClick={clickable ? () => onSelectTicker(e.etf_ticker) : undefined}
            data-testid={`held-by-etf-row-${i}`}
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline gap-1.5">
                <span className="text-xs font-mono font-bold text-primary shrink-0">{e.etf_ticker}</span>
                <span className="text-[11px] text-muted-foreground truncate" title={e.etf_name || ''}>{e.etf_name}</span>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-[10px] font-mono text-muted-foreground hidden sm:inline w-14 text-right">{fmtVal(e.value_usd)}</span>
              <div className="w-14 h-2 rounded-full bg-muted overflow-hidden">
                <div className="h-full rounded-full bg-sky-500/70" style={{ width: `${Math.min(((e.weight_pct || 0) / maxPct) * 100, 100)}%` }} />
              </div>
              <span className="text-xs font-mono font-semibold w-12 text-right">{e.weight_pct != null ? `${e.weight_pct.toFixed(2)}%` : '--'}</span>
            </div>
          </div>
        );
      })}
      <div className="flex items-center justify-between pt-1.5 pb-1">
        {data.etfs.length > 8 ? (
          <button
            onClick={() => setExpanded((v) => !v)}
            className="text-[11px] font-semibold text-primary hover:underline"
            data-testid="held-by-etfs-expand-btn"
          >
            {expanded ? 'Show top 8' : `Show top ${data.etfs.length}`}
          </button>
        ) : <span />}
        <span className="text-[9px] text-muted-foreground">Source: SEC EDGAR Form N-PORT</span>
      </div>
    </div>
  );
};
